import { useEffect, useRef } from 'react'

import { useMeQuery } from '@Store/api'

import LargeMenu from './large-menu'
import MobileMenu from './mobile-menu/mobile-menu'
import SmallMenu from './small-menu'
import { userOnboardingCompletedSelector } from '../../store/api/auth'
import { useAppSelector } from '../../store/hooks'
import {
  readStoredSidebarExpanded,
  useLayoutActions,
  useLayoutState,
} from '../../store/slices/layoutSlice'

function LeftMenu(): JSX.Element {
  const largeReportsButtonRef = useRef<HTMLButtonElement>(null)
  const { data: meData, isLoading: meLoading } = useMeQuery()

  const isOnboardingCompleted = useAppSelector(userOnboardingCompletedSelector)

  const { sidebarExpanded } = useLayoutState()
  const { setSidebarExpanded, toggleSidebar } = useLayoutActions()

  useEffect(() => {
    // Wait for the profile so we don't briefly collapse an expanded sidebar
    // before onboarding status is known (the selector reads false while
    // loading).
    if (meLoading || !meData) return

    if (isOnboardingCompleted !== 1) {
      // Onboarding incomplete: the sidebar isn't usable yet, so force it
      // collapsed without recording a preference.
      setSidebarExpanded(false)
      return
    }

    // Onboarding complete: honor the user's saved choice, defaulting to
    // expanded when they've never toggled it.
    setSidebarExpanded(readStoredSidebarExpanded() ?? true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingCompleted, meLoading, meData])

  return (
    <>
      {sidebarExpanded ? (
        <LargeMenu
          toggleMenu={toggleSidebar}
          reportsRef={largeReportsButtonRef}
          meData={meData}
          meLoading={meLoading}
        />
      ) : (
        <SmallMenu
          isOnboardingCompleted={isOnboardingCompleted === 1}
          toggleMenu={() => {
            isOnboardingCompleted === 1 ? toggleSidebar() : false
          }}
          onClickReports={() => {
            toggleSidebar()
            setTimeout(() => largeReportsButtonRef.current?.click())
          }}
        />
      )}
      <MobileMenu
        isOnboardingCompleted={isOnboardingCompleted === 1}
        meData={meData}
        meLoading={meLoading}
      />
    </>
  )
}

export default LeftMenu
